(function () {
  'use strict';

  if (window.__udbDiscountApplyGuardLoaded) return;
  window.__udbDiscountApplyGuardLoaded = true;

  const SAVINGS_EPSILON = 0.001;
  const PRE_BLOCK_SCENARIOS = new Set(['better_existing', 'below_minimum']);
  const AOV_INACTIVE_WARNING_CODES = new Set([
    'DISCOUNT_CURRENTLY_INACTIVE',
    'DISCOUNT_NOT_FOUND',
    'DISCOUNT_EXPIRED',
  ]);
  const AOV_USAGE_LIMIT_CODES = new Set([
    'DISCOUNT_USAGE_LIMIT_REACHED',
    'CUSTOMER_USAGE_LIMIT_REACHED',
  ]);
  const AOV_STACKING_LIMIT_CODES = new Set([
    'MAXIMUM_DISCOUNT_CODE_LIMIT_REACHED',
    'DISCOUNT_LIMIT_REACHED',
  ]);
  const AOV_NOT_HONOURED_WARNING = 'DISCOUNT_CODE_NOT_HONOURED';

  function toNumber(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function normalizeTier(rawTier = {}) {
    const minimumRaw = toNumber(rawTier.minimum ?? rawTier.minimumAmount, 0);
    const discountRaw = toNumber(rawTier.discountValue, 0);
    const CENTS_THRESHOLD = 10000;

    return {
      minimum: minimumRaw >= CENTS_THRESHOLD ? minimumRaw : Math.round(minimumRaw * 100),
      discountType: rawTier.discountType === 'percentage' ? 'percentage' : 'fixed',
      discountValue: discountRaw >= CENTS_THRESHOLD ? discountRaw / 100 : discountRaw,
    };
  }

  function normalizeTiers(rawTiers = []) {
    return (Array.isArray(rawTiers) ? rawTiers : [])
      .map((tier) => normalizeTier(tier))
      .sort((left, right) => left.minimum - right.minimum);
  }

  function getLineOriginalUnitCents(item) {
    if (window.UdbVariantPrice?.getLineAdminVariantUnitCents) {
      return window.UdbVariantPrice.getLineAdminVariantUnitCents(item);
    }

    const quantity = Math.max(1, toNumber(item?.quantity, 0));
    const originalLineCents = toNumber(item?.original_line_price, 0);
    if (originalLineCents > 0) {
      return Math.round(originalLineCents / quantity);
    }

    const originalCents = toNumber(item?.original_price, 0);
    if (originalCents > 0) return originalCents;

    return toNumber(item?.price, 0);
  }

  function getLineCatalogSubtotalCents(item) {
    if (window.UdbVariantPrice?.getLineAdminVariantLineCents) {
      return window.UdbVariantPrice.getLineAdminVariantLineCents(item);
    }

    const originalLineCents = toNumber(item?.original_line_price, 0);
    if (originalLineCents > 0) return originalLineCents;

    const quantity = Math.max(0, toNumber(item?.quantity, 0));
    if (!quantity) return 0;

    return getLineOriginalUnitCents(item) * quantity;
  }

  function getTierBasisSubtotalCents(cart) {
    window.UdbVariantPrice?.seedCacheFromCart?.(cart);

    if (window.UdbVariantPrice?.getTierBasisSubtotalCents) {
      return window.UdbVariantPrice.getTierBasisSubtotalCents(cart);
    }

    const items = cart?.items;
    if (!Array.isArray(items) || !items.length) {
      return toNumber(cart?.items_subtotal_price, 0);
    }

    return items.reduce((sum, item) => sum + getLineCatalogSubtotalCents(item), 0);
  }

  function calculateTierSavings(tiers, tierBasisSubtotalCents) {
    const subtotalMajor = Math.max(0, toNumber(tierBasisSubtotalCents, 0)) / 100;
    let activeTier = null;

    for (const tier of tiers) {
      if (subtotalMajor >= tier.minimum / 100) {
        activeTier = tier;
      }
    }

    if (!activeTier) return 0;

    if (activeTier.discountType === 'percentage') {
      return Math.round(((subtotalMajor * activeTier.discountValue) / 100) * 100) / 100;
    }

    return Math.min(activeTier.discountValue, subtotalMajor);
  }

  function getLineItemSaleSavingsCents(item) {
    const quantity = Math.max(0, toNumber(item.quantity, 0));
    if (!quantity) return 0;

    const priceCents = toNumber(item.price, 0);
    const compareAtCents = toNumber(item.compare_at_price, 0);
    if (compareAtCents > priceCents) {
      return (compareAtCents - priceCents) * quantity;
    }

    const originalLineCents = toNumber(item.original_line_price, 0);
    const finalLineCents = toNumber(item.final_line_price ?? item.line_price, 0);
    if (originalLineCents > finalLineCents) {
      return originalLineCents - finalLineCents;
    }

    const originalUnitCents = toNumber(item.original_price, 0);
    if (originalUnitCents > priceCents) {
      return (originalUnitCents - priceCents) * quantity;
    }

    return 0;
  }

  function getCompareAtSavings(cart) {
    const items = cart?.items;
    if (!Array.isArray(items) || !items.length) return 0;

    return items.reduce((sum, item) => sum + getLineItemSaleSavingsCents(item), 0);
  }

  function getCartLevelDiscountSavings(cart) {
    const applications = cart?.cart_level_discount_applications;
    if (Array.isArray(applications) && applications.length) {
      return applications.reduce(
        (sum, application) => sum + toNumber(application.total_allocated_amount, 0),
        0,
      );
    }

    const baseSubtotalCents = toNumber(cart?.items_subtotal_price, 0);
    const totalCents = toNumber(cart?.total_price, 0);
    return Math.max(0, baseSubtotalCents - totalCents);
  }

  function resolveProjectedTotal(tierBasisMajor, udbSavings) {
    return Math.max(0, tierBasisMajor - udbSavings);
  }

  function resolveCheckoutDiscountAmount(tierBasisMajor, saleSubtotalMajor, udbSavings) {
    const projectedTotal = resolveProjectedTotal(tierBasisMajor, udbSavings);
    const checkoutDiscount = saleSubtotalMajor - projectedTotal;
    return Math.round(Math.max(0, Math.min(saleSubtotalMajor, checkoutDiscount)) * 100) / 100;
  }

  function normalizeDiscountCode(code) {
    return String(code || '').trim().toUpperCase();
  }

  function shouldBlockAsBetterExisting(cart, tiers, code) {
    const comparison = compareDiscountOptions(tiers, cart);
    if (comparison.shouldApplyUdb) {
      return false;
    }

    return comparison.winner === 'existing';
  }

  function isDiscountCodeAppliedOnCart(cart, code) {
    const normalized = normalizeDiscountCode(code);
    if (!normalized) return false;

    return (Array.isArray(cart?.discount_codes) ? cart.discount_codes : []).some(
      (entry) =>
        normalizeDiscountCode(entry?.code) === normalized && entry.applicable !== false,
    );
  }

  function findAppliedUdbCampaign(cart) {
    const config = readStorefrontConfig();
    if (!config) return null;

    const codes = Array.isArray(cart?.discount_codes) ? cart.discount_codes : [];
    for (const entry of codes) {
      if (!entry || entry.applicable === false) continue;

      const campaign = resolveCampaignForCode(config, entry.code);
      if (campaign?.tiers?.length) {
        return {
          code: String(entry.code || '').trim(),
          campaign,
        };
      }
    }

    return null;
  }

  function isUdbDiscountApplication(application, code) {
    if (!application || !code) return false;

    const normalized = normalizeDiscountCode(code);
    const fields = [application.title, application.code, application.description].map((value) =>
      normalizeDiscountCode(value),
    );

    return fields.some((value) => value && (value === normalized || value.includes(normalized)));
  }

  function getNonUdbCartLevelSavingsCents(cart, udbCode) {
    const applications = cart?.cart_level_discount_applications;
    if (!Array.isArray(applications)) return 0;

    return applications.reduce((sum, application) => {
      if (isUdbDiscountApplication(application, udbCode)) {
        return sum;
      }

      return sum + toNumber(application.total_allocated_amount, 0);
    }, 0);
  }

  function evaluateUdbTierWin(cart) {
    const applied = findAppliedUdbCampaign(cart);
    if (!applied) return null;

    const { code, campaign } = applied;
    const tierBasisCents = getTierBasisSubtotalCents(cart);
    const udbSavingsMajor = calculateTierSavings(campaign.tiers, tierBasisCents);
    if (udbSavingsMajor <= 0) return null;

    const tierBasisMajor = tierBasisCents / 100;
    const saleSubtotalCents = toNumber(cart?.items_subtotal_price, 0);
    const saleSubtotalMajor = saleSubtotalCents / 100;
    const projectedTotal = resolveProjectedTotal(tierBasisMajor, udbSavingsMajor);
    const checkoutDiscountMajor = resolveCheckoutDiscountAmount(
      tierBasisMajor,
      saleSubtotalMajor,
      udbSavingsMajor,
    );
    const otherCartLevelMajor = getNonUdbCartLevelSavingsCents(cart, code) / 100;
    const winsOverOther = udbSavingsMajor > otherCartLevelMajor + SAVINGS_EPSILON;
    const codeApplied = isDiscountCodeAppliedOnCart(cart, code);

    return {
      code,
      campaign,
      winsOverOther,
      isActive: codeApplied && winsOverOther,
      udbSavingsMajor,
      tierBasisMajor,
      saleSubtotalMajor,
      projectedTotal,
      checkoutDiscountMajor,
      projectedTotalCents: Math.round(projectedTotal * 100),
      checkoutDiscountCents: Math.round(checkoutDiscountMajor * 100),
    };
  }

  function normalizeCartForUdbTier(cart) {
    const state = evaluateUdbTierWin(cart);
    if (!state?.isActive) return cart;

    const saleSubtotalCents = toNumber(cart?.items_subtotal_price, 0);
    const cartLevelSavingsCents = getCartLevelDiscountSavings(cart);
    const otherChargesCents = Math.max(
      0,
      toNumber(cart?.total_price, 0) - saleSubtotalCents + cartLevelSavingsCents,
    );
    const applications = Array.isArray(cart?.cart_level_discount_applications)
      ? cart.cart_level_discount_applications
      : [];
    const udbApplications = applications.filter((application) =>
      isUdbDiscountApplication(application, state.code),
    );
    const normalizedApplications = (udbApplications.length ? udbApplications : applications.slice(0, 1)).map(
      (application, index) => ({
        ...application,
        type: application.type || 'discount_code',
        title: application.title || state.code,
        total_allocated_amount: String(
          index === 0 ? state.checkoutDiscountCents : toNumber(application.total_allocated_amount, 0),
        ),
      }),
    );

    return {
      ...cart,
      items: Array.isArray(cart.items) ? [...cart.items] : cart.items,
      discount_codes: (Array.isArray(cart.discount_codes) ? cart.discount_codes : []).filter(
        (entry) =>
          entry &&
          entry.applicable !== false &&
          normalizeDiscountCode(entry.code) === normalizeDiscountCode(state.code),
      ),
      cart_level_discount_applications: normalizedApplications,
      total_price: state.projectedTotalCents + otherChargesCents,
      total_discount: String(state.checkoutDiscountCents),
    };
  }

  function compareDiscountOptions(tiers, cart) {
    const baseSubtotalCents = toNumber(cart?.items_subtotal_price, 0);
    const tierBasisSubtotalCents = getTierBasisSubtotalCents(cart);
    const currentTotalCents = toNumber(cart?.total_price, 0) || baseSubtotalCents;
    const compareAtSavingsCents = getCompareAtSavings(cart);
    const cartLevelSavingsCents = getCartLevelDiscountSavings(cart);
    const existingSavingsCents = cartLevelSavingsCents;
    const baseSubtotal = baseSubtotalCents / 100;
    const tierBasisMajor = tierBasisSubtotalCents / 100;
    const existingSavings = existingSavingsCents / 100;
    const currentTotal = currentTotalCents / 100;
    const udbSavings = calculateTierSavings(tiers, tierBasisSubtotalCents);
    const projectedTotal = resolveProjectedTotal(tierBasisMajor, udbSavings);
    const beatsExistingSavings = udbSavings > existingSavings + SAVINGS_EPSILON;
    const beatsCurrentTotal = projectedTotal < currentTotal - SAVINGS_EPSILON;

    return {
      shouldApplyUdb: beatsExistingSavings && beatsCurrentTotal,
      winner:
        beatsExistingSavings && beatsCurrentTotal
          ? 'udb'
          : existingSavings > SAVINGS_EPSILON
            ? 'existing'
            : 'none',
    };
  }

  function readStorefrontConfig() {
    const node = document.querySelector('[data-udb-storefront-config]');
    if (!node) return null;

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      return null;
    }
  }

  function resolveCampaignForCode(config, code) {
    if (!config || !code) return null;

    const normalized = String(code).trim().toUpperCase();
    const campaigns = Array.isArray(config.campaigns) ? config.campaigns : [];

    for (const campaign of campaigns) {
      if (campaign?.enabled === false) continue;
      const campaignCode = String(campaign?.code || '').trim().toUpperCase();
      if (campaignCode && campaignCode === normalized) {
        return {
          code: campaign.code,
          tiers: normalizeTiers(campaign.tiers || []),
        };
      }
    }

    const rootCode = String(config.code || '').trim().toUpperCase();
    if (rootCode && rootCode === normalized) {
      const rootCampaign =
        campaigns.find((campaign) => {
          const campaignCode = String(campaign?.code || '').trim().toUpperCase();
          return campaignCode === rootCode;
        }) || campaigns[0];

      return {
        code: config.code,
        tiers: normalizeTiers(rootCampaign?.tiers || []),
      };
    }

    return null;
  }

  function getRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  async function fetchCart() {
    const response = await fetch(`${getRoot()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error('Unable to fetch cart');
    }

    return response.json();
  }

  function isBelowMinimumTier(tiers, cartSubtotalCents) {
    if (!tiers.length) return false;
    return toNumber(cartSubtotalCents, 0) < tiers[0].minimum;
  }

  function isUsableTranslation(value) {
    const text = String(value || '').trim();
    if (!text) return false;
    if (/translation missing/i.test(text)) return false;
    if (/^cart\.udb\./.test(text)) return false;
    return true;
  }

  function getDiscountMessages() {
    return {};
  }

  function getMessageForScenario() {
    return '';
  }

  function buildDiscountOutcome(scenario, code, config, options = {}) {
    return {
      scenario,
      reason: scenario,
      code: code || '',
      message: '',
      blockPreApply: Boolean(options.blockPreApply),
      isUdbCode: options.isUdbCode !== false,
    };
  }

  function classifyDiscountScenario(cart, code, graphqlHints = {}) {
    const normalizedCode = String(code || '').trim();
    if (!normalizedCode) {
      return buildDiscountOutcome('unknown_code', '', readStorefrontConfig(), {
        isUdbCode: false,
      });
    }

    const config = readStorefrontConfig();
    if (!config?.enabled) {
      return null;
    }

    const campaign = resolveCampaignForCode(config, normalizedCode);
    if (!campaign?.tiers?.length) {
      return null;
    }

    if (graphqlHints.expired) {
      return buildDiscountOutcome('expired', campaign.code || normalizedCode, config);
    }

    if (graphqlHints.usageLimit) {
      return buildDiscountOutcome('usage_limit', campaign.code || normalizedCode, config);
    }

    const tierBasisSubtotalCents = getTierBasisSubtotalCents(cart);
    if (isBelowMinimumTier(campaign.tiers, tierBasisSubtotalCents)) {
      return buildDiscountOutcome('below_minimum', campaign.code || normalizedCode, config, {
        blockPreApply: true,
      });
    }

    if (shouldBlockAsBetterExisting(cart, campaign.tiers, normalizedCode)) {
      return buildDiscountOutcome('better_existing', campaign.code || normalizedCode, config, {
        blockPreApply: true,
      });
    }

    if (graphqlHints.usageLimit) {
      return buildDiscountOutcome('usage_limit', campaign.code || normalizedCode, config);
    }

    if (graphqlHints.shopifyRejected || graphqlHints.stackingBlocked) {
      return buildDiscountOutcome('better_existing', campaign.code || normalizedCode, config, {
        blockPreApply: true,
      });
    }

    return null;
  }

  function getBetterDiscountMessage() {
    return '';
  }

  function emitDiscountBlocked(detail = {}) {
    const config = readStorefrontConfig();
    const scenario = detail.scenario || detail.reason || 'better_existing';
    const payload = {
      code: detail.code || '',
      scenario,
      reason: scenario,
      message: detail.message || '',
      at: Date.now(),
    };

    window.__udbLastDiscountBlock = payload;
    window.__udbLastAttemptedDiscountCode = payload.code;

    document.documentElement.dispatchEvent(
      new CustomEvent('udb:discount-blocked', {
        bubbles: true,
        detail: payload,
      }),
    );

    return payload;
  }

  function classifyDiscountFailure(cart, code) {
    return classifyDiscountScenario(cart, code) || {
      reason: 'unknown_code',
      scenario: 'unknown_code',
      code: String(code || '').trim(),
    };
  }

  function evaluatePostApplyRejection(cart, code) {
    if (!code) return null;

    const normalized = String(code).trim().toUpperCase();
    const codes = Array.isArray(cart?.discount_codes) ? cart.discount_codes : [];
    const entry = codes.find(
      (item) => String(item?.code || '').trim().toUpperCase() === normalized,
    );

    if (entry && entry.applicable !== false) {
      return null;
    }

    return classifyDiscountScenario(cart, code);
  }

  function evaluateBetterDiscountBlock(code, cart) {
    const outcome = classifyDiscountScenario(cart, code);
    if (outcome?.scenario !== 'better_existing') {
      return null;
    }
    return outcome;
  }

  function handlePostApplyDiscountResult(cart, attemptedCode) {
    if (!attemptedCode) return false;

    window.__udbLastAttemptedDiscountCode = attemptedCode;
    const rejection = evaluatePostApplyRejection(cart, attemptedCode);
    if (!rejection) {
      return false;
    }

    emitDiscountBlocked(rejection);
    return true;
  }

  async function evaluatePreApplyDiscount(code) {
    const config = readStorefrontConfig();
    if (!config?.enabled) {
      return { allow: true };
    }

    const campaign = resolveCampaignForCode(config, code);
    if (!campaign?.tiers?.length) {
      return { allow: true };
    }

    const cart = await fetchCart();
    const outcome = classifyDiscountScenario(cart, code);
    if (!outcome || !PRE_BLOCK_SCENARIOS.has(outcome.scenario)) {
      return { allow: true };
    }

    return {
      allow: false,
      scenario: outcome.scenario,
      reason: outcome.scenario,
      message: outcome.message,
      code: outcome.code || code,
    };
  }

  async function shouldAllowDiscountCode(code) {
    return evaluatePreApplyDiscount(code);
  }

  function buildBlockedAovApplyDiscountsGraphqlResponse(code) {
    const normalizedCode = String(code || '').trim();
    return {
      data: {
        cartDiscountCodesUpdate: {
          cart: {
            discountCodes: [{ code: normalizedCode, applicable: false }],
          },
          userErrors: [],
          warnings: [],
        },
      },
    };
  }

  async function guardAovApplyDiscountBeforeSend(code) {
    const normalizedCode = String(code || '').trim();
    if (!normalizedCode) {
      return { blocked: false, allow: true };
    }

    window.__udbLastAttemptedDiscountCode = normalizedCode;
    const decision = await evaluatePreApplyDiscount(normalizedCode);

    if (decision.allow) {
      return { blocked: false, allow: true, decision };
    }

    emitDiscountBlocked(decision);

    return {
      blocked: true,
      allow: false,
      decision,
      mockResponse: buildBlockedAovApplyDiscountsGraphqlResponse(decision.code || normalizedCode),
    };
  }

  function isCartUpdateUrl(url, method) {
    return String(method || 'GET').toUpperCase() === 'POST' && /\/cart\/update(\.js)?/i.test(String(url || ''));
  }

  function isCartUpdateRequest(input, init) {
    const url = String(typeof input === 'string' ? input : input?.url || '');
    const method = String(init?.method || 'GET').toUpperCase();
    return isCartUpdateUrl(url, method);
  }

  function parseJsonBody(body) {
    if (!body) return null;

    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch (error) {
        return null;
      }
    }

    if (typeof body === 'object') {
      return body;
    }

    return null;
  }

  function parseRequestBody(init) {
    return parseJsonBody(init?.body);
  }

  function cloneInitWithBody(init, bodyObject) {
    const nextInit = { ...init, body: JSON.stringify(bodyObject) };
    const headers = new Headers(init?.headers || {});
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    nextInit.headers = headers;
    return nextInit;
  }

  async function guardDiscountBody(body) {
    if (!body || typeof body.discount !== 'string' || !body.discount.trim()) {
      return { body, blocked: false, touchedDiscount: false };
    }

    const code = body.discount.trim();
    const decision = await evaluatePreApplyDiscount(code);
    if (decision.allow) {
      window.__udbLastAttemptedDiscountCode = code;
      return { body, blocked: false, touchedDiscount: true, attemptedCode: code };
    }

    emitDiscountBlocked(decision);
    return {
      body: { ...body, discount: '' },
      blocked: true,
      touchedDiscount: true,
      attemptedCode: code,
      message: decision.message,
    };
  }

  async function guardCartUpdate(input, init) {
    const body = parseRequestBody(init);
    const guarded = await guardDiscountBody(body);

    if (!guarded.touchedDiscount) {
      return { input, init, blocked: false };
    }

    if (!guarded.blocked) {
      return { input, init, blocked: false };
    }

    return {
      input,
      init: cloneInitWithBody(init, guarded.body),
      blocked: true,
    };
  }

  function dispatchCartChangeEvents(payload, blocked, touchedDiscount) {
    if (
      payload &&
      (touchedDiscount ||
        Array.isArray(payload.items) ||
        Array.isArray(payload.cart_level_discount_applications))
    ) {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { cart: payload, baseEvent: 'udb-discount-apply-guard' },
        }),
      );
    }

    if (blocked) {
      // Do not emit cart:change without a cart payload — that clears fetchCart
      // and breaks <cart-count> / drawer listeners on item remove.
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  }

  const originalFetch = window.fetch.bind(window);
  window.fetch = async function patchedFetch(input, init) {
    if (isCartUpdateRequest(input, init)) {
      try {
        const guarded = await guardCartUpdate(input, init);
        const response = await originalFetch(guarded.input, guarded.init);

        if (response.ok) {
          try {
            const payload = await response.clone().json();
            const body = parseRequestBody(init);
            const touchedDiscount = body && typeof body.discount === 'string';
            const attemptedCode = touchedDiscount ? body.discount.trim() : '';
            if (!guarded.blocked && touchedDiscount && attemptedCode) {
              const rejected = handlePostApplyDiscountResult(payload, attemptedCode);
              if (!rejected) {
                const codes = Array.isArray(payload?.discount_codes) ? payload.discount_codes : [];
                const hasApplicableCode = codes.some(
                  (entry) => entry && entry.applicable !== false && String(entry.code || '').trim(),
                );
                if (hasApplicableCode) {
                  window.SbbCouponFlow?.completeManualDiscountApply?.(payload);
                }
              }
            }
            dispatchCartChangeEvents(payload, guarded.blocked, touchedDiscount);
          } catch (error) {
            if (guarded.blocked) {
              dispatchCartChangeEvents(null, true, true);
            }
          }
        } else if (guarded.blocked) {
          dispatchCartChangeEvents(null, true, true);
        }

        return response;
      } catch (error) {
        return originalFetch(input, init);
      }
    }

    return originalFetch(input, init);
  };

  const originalXhrOpen = XMLHttpRequest.prototype.open;
  const originalXhrSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function patchedXhrOpen(method, url, ...rest) {
    this.__udbGuardMethod = String(method || 'GET').toUpperCase();
    this.__udbGuardUrl = String(url || '');
    return originalXhrOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function patchedXhrSend(body) {
    const xhr = this;
    const method = xhr.__udbGuardMethod || 'GET';
    const url = xhr.__udbGuardUrl || '';

    if (!isCartUpdateUrl(url, method)) {
      return originalXhrSend.call(xhr, body);
    }

    const parsedBody = parseJsonBody(body);
    if (!parsedBody || typeof parsedBody.discount !== 'string' || !parsedBody.discount.trim()) {
      return originalXhrSend.call(xhr, body);
    }

    guardDiscountBody(parsedBody)
      .then((guarded) => {
        const nextBody = JSON.stringify(guarded.body);

        xhr.addEventListener(
          'loadend',
          () => {
            if (xhr.status < 200 || xhr.status >= 300) {
              if (guarded.blocked) {
                dispatchCartChangeEvents(null, true, guarded.touchedDiscount);
              }
              return;
            }

            try {
              const payload = JSON.parse(xhr.responseText || '{}');
              const attemptedCode = parsedBody.discount.trim();
              if (!guarded.blocked && guarded.touchedDiscount && attemptedCode) {
                const rejected = handlePostApplyDiscountResult(payload, attemptedCode);
                if (!rejected) {
                  const codes = Array.isArray(payload?.discount_codes) ? payload.discount_codes : [];
                  const hasApplicableCode = codes.some(
                    (entry) => entry && entry.applicable !== false && String(entry.code || '').trim(),
                  );
                  if (hasApplicableCode) {
                    window.SbbCouponFlow?.completeManualDiscountApply?.(payload);
                  }
                }
              }
              dispatchCartChangeEvents(payload, guarded.blocked, guarded.touchedDiscount);
            } catch (error) {
              if (guarded.blocked) {
                dispatchCartChangeEvents(null, true, guarded.touchedDiscount);
              }
            }
          },
          { once: true },
        );

        originalXhrSend.call(xhr, nextBody);
      })
      .catch(() => {
        originalXhrSend.call(xhr, body);
      });
  };

  function parseAovApplyDiscountsResponse(payload) {
    if (!payload || typeof payload !== 'object') return null;

    const root = payload.data?.cartDiscountCodesUpdate || payload.cartDiscountCodesUpdate;
    if (!root) return null;

    const discountCodes = Array.isArray(root.cart?.discountCodes)
      ? root.cart.discountCodes
      : Array.isArray(root.cart?.discount_codes)
        ? root.cart.discount_codes
        : [];

    return {
      discountCodes,
      warnings: Array.isArray(root.warnings) ? root.warnings : [],
      userErrors: Array.isArray(root.userErrors) ? root.userErrors : [],
    };
  }

  function extractDiscountCodeFromGraphqlBody(body) {
    if (!body) return '';

    let parsed = body;
    if (typeof body === 'string') {
      try {
        parsed = JSON.parse(body);
      } catch (error) {
        return '';
      }
    }

    const codes = parsed?.variables?.discountCodes;
    if (Array.isArray(codes)) {
      const lastCode = codes[codes.length - 1];
      if (lastCode) return String(lastCode).trim();
    }

    if (parsed?.variables?.discountCode) {
      return String(parsed.variables.discountCode).trim();
    }

    return '';
  }

  function readGraphqlWarningCodes(graphqlResult) {
    const codes = new Set();
    for (const warning of graphqlResult?.warnings || []) {
      const value = String(warning?.code || '').trim();
      if (value) codes.add(value);
    }
    for (const error of graphqlResult?.userErrors || []) {
      const value = String(error?.code || error?.field || '').trim();
      if (value) codes.add(value);
    }
    return codes;
  }

  async function resolveAovDiscountApplyOutcome(graphqlResult, attemptedCode) {
    const code = String(attemptedCode || '').trim();
    if (!code || !graphqlResult) {
      return null;
    }

    const config = readStorefrontConfig();
    const campaign = resolveCampaignForCode(config, code);
    if (!campaign?.tiers?.length) {
      return null;
    }

    const normalized = code.toUpperCase();
    const warningCodes = readGraphqlWarningCodes(graphqlResult);
    const hasInactiveWarning = [...warningCodes].some((value) => AOV_INACTIVE_WARNING_CODES.has(value));
    const hasUsageLimitWarning = [...warningCodes].some((value) => AOV_USAGE_LIMIT_CODES.has(value));
    const hasStackingLimitWarning = [...warningCodes].some((value) => AOV_STACKING_LIMIT_CODES.has(value));

    const entry = (graphqlResult.discountCodes || []).find(
      (item) => String(item?.code || '').trim().toUpperCase() === normalized,
    );

    if (entry && entry.applicable !== false) {
      return null;
    }

    const cart = await fetchCart();

    if (hasInactiveWarning) {
      return classifyDiscountScenario(cart, code, { expired: true });
    }

    if (hasUsageLimitWarning) {
      return classifyDiscountScenario(cart, code, { usageLimit: true });
    }

    const notHonoured = warningCodes.has(AOV_NOT_HONOURED_WARNING);
    return classifyDiscountScenario(cart, code, {
      shopifyRejected: notHonoured || Boolean(entry && entry.applicable === false),
      stackingBlocked: hasStackingLimitWarning,
    });
  }

  window.UdbDiscountApplyGuard = {
    evaluatePreApplyDiscount,
    shouldAllowDiscountCode,
    guardAovApplyDiscountBeforeSend,
    buildBlockedAovApplyDiscountsGraphqlResponse,
    compareDiscountOptions,
    classifyDiscountFailure,
    classifyDiscountScenario,
    evaluatePostApplyRejection,
    evaluateBetterDiscountBlock,
    handlePostApplyDiscountResult,
    getBetterDiscountMessage,
    getMessageForScenario,
    getDiscountMessages,
    isUsableTranslation,
    getCompareAtSavings,
    getTierBasisSubtotalCents,
    resolveCheckoutDiscountAmount,
    evaluateUdbTierWin,
    normalizeCartForUdbTier,
    findAppliedUdbCampaign,
    readStorefrontConfig,
    resolveCampaignForCode,
    parseAovApplyDiscountsResponse,
    extractDiscountCodeFromGraphqlBody,
    resolveAovDiscountApplyOutcome,
  };
})();
